import { useLazyQuery, gql } from '@apollo/client';
import { useContext, useEffect, useMemo } from 'react';
import i18next from 'i18next';
import EtimClassCodeContext from '../context/EtimClassCodes';

const METAOBJECT_FIELDS = `
  name: field(key: "name") {
    value
  }
  tooltip: field(key: "tooltip") {
    value
  }
  units: field(key: "units") {
    value
  }
  values: field(key: "values") {
    value
  }
  `;

const GET_METAOBJECT_BY_HANDLE = gql`
  query getMetaobjectByHandle($handle: String!, $fallback_handle: String!, $fetch_fallback: Boolean!) {
    etim_tooltip: metaobject(handle: { type: "etim_tooltip", handle: $handle }) {
      id
      handle
      ${METAOBJECT_FIELDS}
    }
    fallback: metaobject(handle: { type: "etim_tooltip", handle: $fallback_handle }) @include(if: $fetch_fallback) {
      id
      handle
      ${METAOBJECT_FIELDS}
    }
  }
`;

function getDataOrFallback(data: any, key: string) {
  return data?.etim_tooltip?.[key]?.value ?? data?.fallback?.[key]?.value;
}

const useJsonMap = (data: any, field: string) => {
  return useMemo(() => {
    if (!data) {
      return new Map<string, string>();
    }

    let fieldValue = data?.etim_tooltip?.[field]?.value;

    if (!fieldValue || fieldValue === '[]') {
      fieldValue = data?.fallback?.[field]?.value;
    }

    if (!fieldValue || fieldValue === '[]') {
      fieldValue = '{}';
    }

    const caseSensitiveMap = JSON.parse(fieldValue);
    const lowerCaseMap = new Map<string, string>();

    for (const [key, value] of Object.entries(caseSensitiveMap)) {
      lowerCaseMap.set(key.toLowerCase(), value as string);
    }

    return lowerCaseMap;
  }, [data, field]);
};

export const useEtimTranslation = (etimCode: string, delay = 0) => {
  const etimClassCodes = useContext(EtimClassCodeContext);

  const keyCleaned = useMemo(() => {
    if (!etimCode) {
      return '';
    }

    if (etimCode.startsWith('price')) {
      return 'price';
    }

    if (etimCode.startsWith('collections')) {
      return 'collections';
    }

    return etimCode.replace('named_tags.', '').trim().toLowerCase();
  }, [etimCode]);

  const [getEtimTooltip, { loading, error, data }] = useLazyQuery(GET_METAOBJECT_BY_HANDLE);

  useEffect(() => {
    if (
      keyCleaned &&
      !['vendor', 'collections', 'price', 'product', 'tags_algolia', 'availability'].includes(keyCleaned)
    ) {
      const keySplit = keyCleaned.split('-');

      if (keySplit.length === 1 && etimClassCodes.length === 1) {
        keySplit.unshift(etimClassCodes[0].toLowerCase());
      }

      const variables = {
        handle: keySplit.join('-'),
        fetch_fallback: keySplit.length === 2,
        fallback_handle: keySplit.length === 2 ? keySplit[1] : '',
      };

      if (delay) {
        setTimeout(() => getEtimTooltip({ variables }), delay);
      } else {
        getEtimTooltip({ variables });
      }
    }
  }, [etimCode, etimClassCodes]);

  const unitMap = useJsonMap(data, 'units');
  const valuesMap = useJsonMap(data, 'values');

  const unit = useMemo(() => {
    for (const etimClass of etimClassCodes) {
      const tentativeUnit = unitMap.get(etimClass.toLowerCase());

      if (tentativeUnit) {
        return tentativeUnit;
      }
    }

    // Use the feature name itself as a fallback
    const unitFromFeature = unitMap.get(keyCleaned);

    if (unitFromFeature) {
      return unitFromFeature;
    }

    return '';
  }, [unitMap, etimClassCodes, keyCleaned]);

  return useMemo(() => {
    if (!keyCleaned) {
      return {};
    }

    if (['vendor', 'collections', 'price', 'product', 'availability'].includes(keyCleaned)) {
      if (window.theme.featureFlags.hierarchyCollection && keyCleaned === 'collections') {
        return {
          name: theme.locales.collections,
          translateEtimValue: (etimValueCode: string) => {
            return etimValueCode
              .split(' > ')
              .map((value) => i18next.t(value, { defaultValue: value, ns: 'collections' }))
              .join(' > ');
          },
        };
      }

      return {
        name: theme.locales[keyCleaned as keyof typeof theme.locales],
      };
    }

    if (keyCleaned === 'tags_algolia') {
      return {
        name: theme.locales.collections,
      };
    }

    if (loading || error) {
      return {
        name: keyCleaned,
      };
    }

    return {
      loaded: true,
      name: getDataOrFallback(data, 'name') ?? keyCleaned,
      tooltip: getDataOrFallback(data, 'tooltip'),
      image: getDataOrFallback(data, 'image'),
      content: getDataOrFallback(data, 'content'),
      unit,
      translateEtimValue: (etimValueCode: string) => {
        let translatedValue;

        if (etimValueCode && typeof etimValueCode === 'string') {
          if (etimValueCode.toLocaleLowerCase() === 'yes') {
            translatedValue = theme.locales.trueLabel;
          } else if (etimValueCode.toLocaleLowerCase() === 'no') {
            translatedValue = theme.locales.falseLabel;
          } else if (etimValueCode.toLowerCase().match(/^[te]v\d{6}$/)) {
            translatedValue = valuesMap.get(etimValueCode.toLowerCase());
          }
        }

        return (translatedValue ?? etimValueCode) as string;
      },
    };
  }, [keyCleaned, loading, error, data, unit]);
};
